import { isPlatformBrowser } from '@angular/common';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { DealerFeature } from '../+state';
import { Platform, PLATFORM_TOKEN } from '../providers/platform.token';
import { extractAdIdFromVehicleSlug, type FavoritePlatform, type FavoriteScope } from './vehicle-favorites.utility';

export type { FavoritePlatform, FavoriteScope } from './vehicle-favorites.utility';

export interface VehicleFavoriteEntry {
    slug: string;
    scope: FavoritePlatform;
    dealerId?: string;
    dealerSlug?: string;
    savedAt: string;
}

interface FavoritesStorage {
    hcm: Record<string, VehicleFavoriteEntry>;
    hws: Record<string, Record<string, VehicleFavoriteEntry>>;
}

export interface VehicleFavoriteInput {
    slug: string;
}

const STORAGE_KEY = 'hyundai-vehicle-favorites';

@Injectable({
    providedIn: 'root',
})
export class VehicleFavoritesService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly platform = inject(PLATFORM_TOKEN);
    private readonly store = inject(Store);
    private readonly loadedDealer = this.store.selectSignal(DealerFeature.state.selectLoadedDealer);
    private readonly favoritesChangeTickState = signal(0);
    readonly favoritesChangeTick = this.favoritesChangeTickState.asReadonly();

    readonly favoritesCount = computed(() => {
        this.favoritesChangeTickState();
        return this.getFavorites(this.getScope()).length;
    });

    isFavorite(scope: FavoriteScope, vehicleSlug: string): boolean {
        if (!this.canUseStorage()) {
            return false;
        }
        const storage = this.readStorage();
        if (scope.platform === 'hcm') {
            return Boolean(storage.hcm[vehicleSlug]);
        }
        if (!scope.dealerId) {
            return false;
        }
        return Boolean(storage.hws[scope.dealerId]?.[vehicleSlug]);
    }

    toggle(scope: FavoriteScope, vehicle: VehicleFavoriteInput): boolean {
        if (!this.canUseStorage()) {
            return false;
        }

        const storage = this.readStorage();
        const isCurrentlyFavorite = this.isFavorite(scope, vehicle.slug);

        if (scope.platform === 'hcm') {
            if (isCurrentlyFavorite) {
                delete storage.hcm[vehicle.slug];
            } else {
                storage.hcm[vehicle.slug] = this.createEntry(scope, vehicle);
            }
        } else {
            if (!scope.dealerId) {
                return false;
            }

            storage.hws[scope.dealerId] ??= {};

            if (isCurrentlyFavorite) {
                delete storage.hws[scope.dealerId][vehicle.slug];

                if (Object.keys(storage.hws[scope.dealerId]).length === 0) {
                    delete storage.hws[scope.dealerId];
                }
            } else {
                storage.hws[scope.dealerId][vehicle.slug] = this.createEntry(scope, vehicle);
            }
        }

        this.writeStorage(storage);
        this.favoritesChangeTickState.update((tick) => tick + 1);
        return !isCurrentlyFavorite;
    }

    getScope(): FavoriteScope {
        if (this.platform === Platform.HCM) {
            return {
                platform: 'hcm',
            };
        }
        const dealer = this.loadedDealer();
        if (!dealer?.id) {
            return {
                platform: 'hws',
            };
        }
        return {
            platform: 'hws',
            dealerId: dealer.id,
            dealerSlug: dealer.slug,
        };
    }

    getFavorites(scope: FavoriteScope): VehicleFavoriteEntry[] {
        if (!this.canUseStorage()) {
            return [];
        }
        const storage = this.readStorage();
        if (scope.platform === 'hcm') {
            return Object.values(storage.hcm);
        }
        if (!scope.dealerId) {
            return [];
        }
        return Object.values(storage.hws[scope.dealerId] ?? {});
    }

    pruneStale(scope: FavoriteScope, vehicles: { slug: string }[]): void {
        if (!this.canUseStorage()) {
            return;
        }

        const returnedSlugs = new Set(vehicles.map((vehicle) => vehicle.slug));
        const returnedAdIds = new Set(vehicles.map((vehicle) => extractAdIdFromVehicleSlug(vehicle.slug)));
        const shouldKeep = (entry: VehicleFavoriteEntry): boolean =>
            returnedSlugs.has(entry.slug) || returnedAdIds.has(extractAdIdFromVehicleSlug(entry.slug));

        const storage = this.readStorage();
        let changed = false;

        if (scope.platform === 'hcm') {
            for (const slug of Object.keys(storage.hcm)) {
                const entry = storage.hcm[slug];
                if (entry && !shouldKeep(entry)) {
                    delete storage.hcm[slug];
                    changed = true;
                }
            }
        } else if (scope.dealerId) {
            const dealerFavorites = storage.hws[scope.dealerId];
            if (dealerFavorites) {
                for (const slug of Object.keys(dealerFavorites)) {
                    const entry = dealerFavorites[slug];
                    if (entry && !shouldKeep(entry)) {
                        delete dealerFavorites[slug];
                        changed = true;
                    }
                }
                if (Object.keys(dealerFavorites).length === 0) {
                    delete storage.hws[scope.dealerId];
                }
            }
        }

        if (changed) {
            this.writeStorage(storage);
            this.favoritesChangeTickState.update((tick) => tick + 1);
        }
    }

    private createEntry(scope: FavoriteScope, vehicle: VehicleFavoriteInput): VehicleFavoriteEntry {
        return {
            slug: vehicle.slug,
            scope: scope.platform,
            dealerId: scope.dealerId,
            savedAt: new Date().toISOString(),
        };
    }

    private canUseStorage(): boolean {
        return isPlatformBrowser(this.platformId);
    }

    private readStorage(): FavoritesStorage {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return { hcm: {}, hws: {} };
            }

            const parsed = JSON.parse(raw) as Partial<FavoritesStorage>;
            return {
                hcm: parsed.hcm ?? {},
                hws: parsed.hws ?? {},
            };
        } catch {
            return { hcm: {}, hws: {} };
        }
    }

    private writeStorage(storage: FavoritesStorage): void {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(storage));
    }
}
